
import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'wouter'
import { cn } from '@/lib/utils'
import { Spotlight } from '@/components/ui/spotlight'
import {
  ArrowRight, Check, Flame, Globe, Server, Cpu, Layers,
  Database, Terminal, Copy, CheckCheck, Zap, Lock, Code2,
  ChevronRight, BarChart3, RefreshCw, Shield, Activity,
  GitBranch, Package, Workflow,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'

// ─── Steps ────────────────────────────────────────────────────────────────────
const STEPS = [
  { id: 'project',   label: 'Name your project' },
  { id: 'usecase',  label: 'What are you building?' },
  { id: 'provision', label: 'Provision' },
  { id: 'connect',  label: 'Connect' },
]

// ─── Use cases ────────────────────────────────────────────────────────────────
const USE_CASES = [
  {
    id: 'web', icon: Globe, title: 'Web Application',
    description: 'User data, sessions, and app state at the edge — globally distributed, always fast.',
    starterSql: `CREATE TABLE users (\n  id TEXT PRIMARY KEY DEFAULT (lower(hex(randomblob(16)))),\n  email TEXT NOT NULL UNIQUE,\n  name TEXT,\n  created_at TEXT DEFAULT (datetime('now'))\n);`,
  },
  {
    id: 'api', icon: Server, title: 'API Backend',
    description: 'Zero connection pooling, sub-10ms reads for REST or GraphQL at any scale.',
    starterSql: `CREATE TABLE events (\n  id TEXT PRIMARY KEY DEFAULT (lower(hex(randomblob(16)))),\n  type TEXT NOT NULL,\n  payload TEXT,\n  created_at TEXT DEFAULT (datetime('now'))\n);`,
  },
  {
    id: 'edge', icon: Cpu, title: 'Edge Worker',
    description: 'Run SQL directly inside a Cloudflare Worker — no round-trips, no latency tax.',
    starterSql: `CREATE TABLE cache (\n  key TEXT PRIMARY KEY,\n  value TEXT NOT NULL,\n  expires_at TEXT\n);`,
  },
  {
    id: 'analytics', icon: BarChart3, title: 'Analytics & Logging',
    description: 'Append-only event streams, usage logs, and time-series data at scale.',
    starterSql: `CREATE TABLE logs (\n  id TEXT PRIMARY KEY DEFAULT (lower(hex(randomblob(16)))),\n  level TEXT NOT NULL CHECK (level IN ('info','warn','error')),\n  message TEXT NOT NULL,\n  metadata TEXT,\n  ts TEXT DEFAULT (datetime('now'))\n);`,
  },
  {
    id: 'ai', icon: Zap, title: 'AI Application',
    description: 'Persist conversations, embeddings metadata, and model state at the edge.',
    starterSql: `CREATE TABLE conversations (\n  id TEXT PRIMARY KEY DEFAULT (lower(hex(randomblob(16)))),\n  user_id TEXT NOT NULL,\n  model TEXT NOT NULL,\n  messages TEXT NOT NULL DEFAULT '[]',\n  created_at TEXT DEFAULT (datetime('now'))\n);`,
  },
  {
    id: 'multi', icon: Layers, title: 'Multi-tenant SaaS',
    description: 'Row-level tenant isolation, scoped API keys, per-tenant analytics — built in.',
    starterSql: `CREATE TABLE tenants (\n  id TEXT PRIMARY KEY DEFAULT (lower(hex(randomblob(16)))),\n  slug TEXT NOT NULL UNIQUE,\n  name TEXT NOT NULL,\n  plan TEXT DEFAULT 'free',\n  created_at TEXT DEFAULT (datetime('now'))\n);`,
  },
]

// ─── Syntax highlighting ───────────────────────────────────────────────────────
type Token = { type: 'keyword' | 'string' | 'comment' | 'number' | 'function' | 'plain'; value: string }

const SQL_KEYWORDS = new Set(['SELECT','FROM','WHERE','INSERT','INTO','VALUES','CREATE','TABLE','UPDATE','SET','DELETE','PRIMARY','KEY','DEFAULT','NOT','NULL','UNIQUE','CHECK','IN','TEXT','INTEGER','REAL','BLOB','AND','OR','LIMIT','ORDER','BY','ASC','DESC','RETURNING'])
const JS_KEYWORDS  = new Set(['import','export','const','let','var','await','async','return','new','from','default','function','if','else'])

function tokenizeSQL(code: string): Token[] {
  const tokens: Token[] = []
  const words = code.split(/(\s+|[(),;'"]|--[^\n]*)/g).filter(Boolean)
  let inString = false, strChar = ''
  for (const w of words) {
    if (!inString && (w === "'" || w === '"')) { inString = true; strChar = w; tokens.push({ type: 'string', value: w }); continue }
    if (inString) { tokens.push({ type: 'string', value: w }); if (w === strChar) inString = false; continue }
    if (w.startsWith('--')) { tokens.push({ type: 'comment', value: w }); continue }
    if (SQL_KEYWORDS.has(w.toUpperCase())) { tokens.push({ type: 'keyword', value: w }); continue }
    if (/^\d+$/.test(w)) { tokens.push({ type: 'number', value: w }); continue }
    tokens.push({ type: 'plain', value: w })
  }
  return tokens
}

function tokenizeJS(code: string): Token[] {
  const tokens: Token[] = []
  const regex = /(\/\/[^\n]*|`[^`]*`|'[^']*'|"[^"]*"|\b(?:import|export|const|let|var|await|async|return|new|from|default|function|if|else)\b|\b\w+(?=\()|\/\/.*)/g
  let last = 0
  let m: RegExpExecArray | null
  while ((m = regex.exec(code)) !== null) {
    if (m.index > last) tokens.push({ type: 'plain', value: code.slice(last, m.index) })
    const v = m[0]
    if (v.startsWith('//')) tokens.push({ type: 'comment', value: v })
    else if (v.startsWith('`') || v.startsWith("'") || v.startsWith('"')) tokens.push({ type: 'string', value: v })
    else if (JS_KEYWORDS.has(v)) tokens.push({ type: 'keyword', value: v })
    else tokens.push({ type: 'function', value: v })
    last = m.index + v.length
  }
  if (last < code.length) tokens.push({ type: 'plain', value: code.slice(last) })
  return tokens
}

const TOKEN_COLORS: Record<Token['type'], string> = {
  keyword:  'text-[#c792ea]',
  string:   'text-[#c3e88d]',
  comment:  'text-[oklch(0.5_0_0)]',
  number:   'text-[#f78c6c]',
  function: 'text-[#82aaff]',
  plain:    'text-[#cdd3de]',
}

function SyntaxBlock({ code, lang = 'js', className }: { code: string; lang?: 'js' | 'sql' | 'bash' | 'python'; className?: string }) {
  const [copied, setCopied] = useState(false)
  const tokens = lang === 'sql' ? tokenizeSQL(code) : lang === 'js' ? tokenizeJS(code) : null

  return (
    <div className={cn('rounded-xl border border-border bg-[#0d1117] overflow-hidden group relative', className)}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[oklch(0.24_0.006_260)] bg-[oklch(0.12_0.006_260)]">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
        </div>
        <span className="text-[10px] font-mono text-[oklch(0.45_0_0)] uppercase tracking-widest">{lang}</span>
        <button
          onClick={async () => { await navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000) }}
          className="flex items-center gap-1.5 text-[10px] text-[oklch(0.45_0_0)] hover:text-[oklch(0.7_0_0)] transition-colors"
        >
          {copied ? <><CheckCheck className="w-3 h-3 text-[#28c840]" />Copied</> : <><Copy className="w-3 h-3" />Copy</>}
        </button>
      </div>
      <pre className="px-5 py-4 text-[12.5px] font-mono leading-[1.8] overflow-x-auto">
        {tokens
          ? tokens.map((t, i) => <span key={i} className={TOKEN_COLORS[t.type]}>{t.value}</span>)
          : <span className="text-[#cdd3de]">{code}</span>
        }
      </pre>
    </div>
  )
}

// ─── Animated architecture diagram ────────────────────────────────────────────
function ArchDiagram() {
  const [active, setActive] = useState(0)
  const [pulse, setPulse] = useState(false)
  // resetting tracks when we wrap so we can skip the bar transition
  const [resetting, setResetting] = useState(false)

  useEffect(() => {
    const iv = setInterval(() => {
      setPulse(true)
      setTimeout(() => setPulse(false), 600)
      setActive(a => {
        const next = a + 1
        if (next >= 5) {
          // snap bar back to 0 without animation, then let next tick start from 0
          setResetting(true)
          setTimeout(() => setResetting(false), 50)
          return 0
        }
        return next
      })
    }, 1800)
    return () => clearInterval(iv)
  }, [])

  const nodes = [
    { id: 'app',      label: 'Your App',      sub: 'Next.js / React',   icon: Code2,    color: '#82aaff' },
    { id: 'sdk',      label: 'LavaFlow SDK',  sub: 'Web Crypto signing', icon: Lock,     color: 'var(--lava)' },
    { id: 'worker',   label: 'CF Worker',     sub: 'Edge runtime',       icon: Zap,      color: '#c3e88d' },
    { id: 'd1',       label: 'D1 EdgeSQL',    sub: 'Cloudflare D1',      icon: Database, color: '#f78c6c' },
    { id: 'replicas', label: 'Edge Replicas', sub: '300+ PoPs global',   icon: Globe,    color: '#c792ea' },
  ]

  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-border bg-[#0d1117] p-6 select-none">
      {/* Background grid */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />

      {/* Title */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-[#28c840] animate-pulse" />
          <span className="text-[11px] font-mono text-[oklch(0.45_0_0)] uppercase tracking-widest">Live architecture</span>
        </div>
        <div className="flex items-center gap-3 text-[10px] font-mono text-[oklch(0.35_0_0)]">
          <span>{'p99 <8ms'}</span>
          <span>·</span>
          <span className={cn('transition-colors', pulse && 'text-[#c3e88d]')}>req/s ↑</span>
        </div>
      </div>

      {/* Nodes + inline connectors — all in one row so connectors can be absolutely positioned per-gap */}
      <div className="relative flex items-start justify-between gap-1">
        {nodes.map((node, i) => {
          const Icon = node.icon
          const isActive = active === i
          const isEdgeActive = active === i || active === i + 1
          return (
            <div key={node.id} className="flex items-start flex-1">
              {/* Node */}
              <div className={cn('flex flex-col items-center gap-2 transition-all duration-500 w-full', isActive ? 'scale-105' : 'scale-100 opacity-60')}>
                <div
                  className={cn('w-11 h-11 rounded-xl flex items-center justify-center border transition-all duration-500', isActive ? 'border-current' : 'border-[oklch(0.24_0.006_260)]')}
                  style={{
                    color: node.color,
                    backgroundColor: isActive ? `${node.color}18` : 'oklch(0.14 0.006 260)',
                    boxShadow: isActive ? `0 0 20px ${node.color}30` : 'none',
                  }}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <div className="text-center">
                  <p className="text-[11px] font-semibold text-[#cdd3de] leading-tight">{node.label}</p>
                  <p className="text-[9px] text-[oklch(0.4_0_0)] leading-tight mt-0.5">{node.sub}</p>
                </div>
              </div>

              {/* Connector line to next node — sits at icon center height (22px = half of h-11) */}
              {i < nodes.length - 1 && (
                <div className="flex-shrink-0 flex items-start pt-[22px] -mx-0.5 z-10">
                  <div
                    className="h-px w-6 transition-all duration-500"
                    style={{
                      background: isEdgeActive
                        ? `linear-gradient(90deg, ${nodes[i].color}90, ${nodes[i + 1].color}90)`
                        : 'oklch(0.24 0.006 260)',
                      boxShadow: isEdgeActive ? `0 0 6px ${nodes[i].color}60` : 'none',
                    }}
                  />
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Progress bar */}
      <div className="mt-5 flex items-center gap-2">
        <div className="flex-1 h-1 bg-[oklch(0.18_0.005_260)] rounded-full overflow-hidden">
          <div
            className={cn('h-full rounded-full', !resetting && 'transition-all duration-[1800ms] ease-in-out')}
            style={{
              width: resetting ? '0%' : `${(active / (nodes.length - 1)) * 100}%`,
              background: 'linear-gradient(90deg, var(--lava), #c792ea)',
              boxShadow: '0 0 8px var(--lava)',
            }}
          />
        </div>
        <span className="text-[9px] font-mono text-[oklch(0.35_0_0)] w-12 text-right">
          {['Query', 'Sign', 'Verify', 'Execute', 'Return'][active]}
        </span>
      </div>
    </div>
  )
}

// ─── Floating stat pill ───────────────────────────────────────────────────────
function StatPill({ icon: Icon, label, value, color }: { icon: React.ElementType; label: string; value: string; color: string }) {
  return (
    <div className="flex items-center gap-2.5 px-3.5 py-2 rounded-full border border-border bg-card/80 backdrop-blur-sm">
      <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color }} />
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <span className="text-[11px] font-bold text-foreground font-mono">{value}</span>
    </div>
  )
}

// ─── Animated terminal typewriter ─────────────────────────────────────────────
const TERMINAL_LINES = [
  { text: '$ lavaflow db:create my-app', delay: 0,    color: '#cdd3de' },
  { text: '  Provisioning EdgeSQL...', delay: 600,  color: '#82aaff' },
  { text: '  Database bound to 312 edge nodes', delay: 1200, color: '#82aaff' },
  { text: '  Generating scoped API key...', delay: 1800, color: '#82aaff' },
  { text: '  Ready in 1.2s', delay: 2400, color: '#c3e88d' },
  { text: '', delay: 2800, color: '' },
  { text: '$ curl api.lavaflowstreams.pro/query \\', delay: 3000, color: '#cdd3de' },
  { text: "  -H 'Authorization: Bearer lf_...' \\", delay: 3200, color: '#c792ea' },
  { text: "  -d '{\"query\": \"SELECT 1\"}'", delay: 3400, color: '#c3e88d' },
  { text: '', delay: 3600, color: '' },
  { text: '  {"results":[{"1":1}],"meta":{"duration_ms":6}}', delay: 3800, color: '#f78c6c' },
]

function AnimatedTerminal() {
  const [visibleLines, setVisibleLines] = useState(0)

  useEffect(() => {
    let cancelled = false
    async function run() {
      for (let i = 0; i < TERMINAL_LINES.length; i++) {
        await new Promise(r => setTimeout(r, i === 0 ? 400 : TERMINAL_LINES[i].delay - TERMINAL_LINES[i - 1].delay))
        if (cancelled) return
        setVisibleLines(i + 1)
      }
    }
    run()
    return () => { cancelled = true }
  }, [])

  return (
    <div className="rounded-xl border border-[oklch(0.24_0.006_260)] bg-[#0d1117] overflow-hidden">
      <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-[oklch(0.2_0.006_260)] bg-[oklch(0.11_0.006_260)]">
        <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
        <span className="ml-2 text-[10px] font-mono text-[oklch(0.4_0_0)]">terminal</span>
      </div>
      <div className="p-4 font-mono text-[12px] leading-[1.9] min-h-[200px]">
        {TERMINAL_LINES.slice(0, visibleLines).map((line, i) => (
          <div key={i} style={{ color: line.color || 'transparent' }}>
            {line.text || '\u00a0'}
            {i === visibleLines - 1 && (
              <span className="inline-block w-2 h-3.5 ml-0.5 bg-[var(--lava)] align-middle animate-pulse" />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Step indicator ───────────────────────────────────────────────────────────
function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-0 mb-10">
      {STEPS.map((step, i) => (
        <div key={step.id} className="flex items-center">
          <div className="flex items-center gap-2">
            <div className={cn(
              'w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold transition-all duration-300',
              i < current  && 'bg-lava text-lava-foreground',
              i === current && 'bg-lava text-lava-foreground ring-4 ring-lava/20',
              i > current  && 'bg-muted text-muted-foreground',
            )}>
              {i < current ? <Check className="w-3 h-3" /> : i + 1}
            </div>
            <span className={cn(
              'text-xs font-medium hidden sm:block transition-colors',
              i === current ? 'text-foreground' : 'text-muted-foreground',
            )}>
              {step.label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <ChevronRight className={cn('w-4 h-4 mx-2 flex-shrink-0 transition-colors', i < current ? 'text-lava' : 'text-border')} />
          )}
        </div>
      ))}
    </div>
  )
}

// ─── Copy button ──────────────────────────────────────────────────────────────
function CopyButton({ text, className }: { text: string; className?: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={async () => { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000) }}
      className={cn('p-1.5 rounded hover:bg-muted/80 transition-colors text-muted-foreground hover:text-foreground', className)}
    >
      {copied ? <CheckCheck className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  )
}

// ─── Code snippet tabs ────────────────────────────────────────────────────────
type Lang = 'JavaScript' | 'TypeScript' | 'cURL' | 'Python' | 'Worker'
const LANG_ICONS: Record<Lang, string> = {
  JavaScript: 'JS', TypeScript: 'TS', cURL: '$', Python: 'PY', Worker: 'CF',
}

function generateSnippets(apiKey: string, dbName: string) {
  const base = 'https://api.lavaflowstreams.pro'
  return {
    JavaScript: `import { createClient } from '@lavaflow/client'

const db = createClient({
  apiKey: process.env.LAVAFLOW_SECRET,
  database: '${dbName}',
})

// Query your edge database
const { results } = await db.query(
  "SELECT * FROM users WHERE active = ?",
  [1]
)

// Drizzle ORM compatible
import { drizzle } from 'drizzle-orm/lavaflow'
export const drizzleDb = drizzle(db)`,

    TypeScript: `import { createClient, type LavaFlowClient } from '@lavaflow/client'
import { drizzle } from 'drizzle-orm/lavaflow'
import * as schema from './schema'

const client: LavaFlowClient = createClient({
  apiKey: process.env.LAVAFLOW_SECRET!,
  database: '${dbName}',
})

export const db = drizzle(client, { schema })

// Type-safe edge queries
const users = await db.query.users.findMany({
  where: (u, { eq }) => eq(u.active, true),
})`,

    cURL: `# Query your EdgeSQL database
curl -X POST ${base}/query \\
  -H "Authorization: Bearer $LAVAFLOW_SECRET" \\
  -H "Content-Type: application/json" \\
  -d '{"query": "SELECT * FROM users LIMIT 10", "params": []}'

# REST endpoint — works like any SQL database
curl ${base}/rest/users \\
  -H "Authorization: Bearer $LAVAFLOW_SECRET"`,

    Python: `import os
import httpx

client = httpx.AsyncClient(
    base_url="https://api.lavaflowstreams.pro",
    headers={"Authorization": f"Bearer {os.environ['LAVAFLOW_SECRET']}"},
)

# SQLAlchemy compatible via REST adapter
response = await client.post("/query", json={
    "query": "SELECT * FROM users WHERE active = ?",
    "params": [1],
})

data = response.json()
print(data["results"])`,

    Worker: `// wrangler.toml — bind your EdgeSQL database
// [[d1_databases]]
// binding = "DB"
// database_name = "${dbName}"

export default {
  async fetch(request: Request, env: Env) {
    // Native D1 binding — zero latency
    const result = await env.DB
      .prepare("SELECT * FROM users LIMIT 10")
      .all()

    return Response.json(result.results)
  }
}`,
  }
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function GetStartedPage() {
  const [, setLocation] = useLocation()
  const [step, setStep] = useState(0)
  const [projectName, setProjectName] = useState('')
  const [selectedUseCase, setSelectedUseCase] = useState<string | null>(null)
  const [provisioning, setProvisioning] = useState(false)
  const [provisionDone, setProvisionDone] = useState(false)
  const [apiKey, setApiKey] = useState<string | null>(null)
  const [dbName, setDbName] = useState('')
  const [error, setError] = useState('')
  const [codeLang, setCodeLang] = useState<Lang>('JavaScript')
  const [provisionStep, setProvisionStep] = useState(-1)

  const useCase = USE_CASES.find(u => u.id === selectedUseCase)
  const dbSlug = projectName.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'my-project'

  const PROVISION_STEPS = [
    'Allocating D1 database slot',
    'Binding to 312 edge nodes',
    'Generating scoped API key',
    'Applying starter schema',
    'Database live',
  ]

  async function handleProvision() {
    setProvisioning(true)
    setError('')
    setProvisionStep(0)

    // Animate provision steps
    for (let i = 1; i < PROVISION_STEPS.length; i++) {
      await new Promise(r => setTimeout(r, 500))
      setProvisionStep(i)
    }

    try {
      const keyRes = await fetch('/api/keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: `${dbSlug}-key`,
          permissions: ['db:read', 'db:write', 'db:admin'],
          metadata: { project: dbSlug, useCase: selectedUseCase },
        }),
      })
      const keyData = await keyRes.json()
      if (!keyRes.ok) {
        if (keyRes.status === 401) { setLocation('/login'); return }
        setError(keyData.error || 'Provisioning failed')
        setProvisionStep(-1)
        return
      }
      setApiKey(keyData.rawKey)
      setDbName(dbSlug)
      setProvisionDone(true)
      setStep(3)
    } catch {
      setError('Network error — please try again.')
      setProvisionStep(-1)
    } finally {
      setProvisioning(false)
    }
  }

  const snippets = generateSnippets(apiKey ?? 'lf_YOUR_KEY', dbName || dbSlug)

  return (
    <div className="min-h-screen flex flex-col">

      {/* ── Hero — step 0 only ─────────────────────────────────── */}
      {step === 0 && (
        <div className="relative flex flex-col items-center px-4 pt-20 pb-8 text-center overflow-hidden">
          {/* Deep background layers — z-0 keeps them behind all content */}
          <div
            className="absolute inset-0 pointer-events-none z-0"
            style={{
              background: 'radial-gradient(ellipse 80% 60% at 50% 0%, oklch(0.14 0.015 38) 0%, oklch(0.08 0.008 260) 55%, oklch(0.06 0 0) 100%)',
            }}
          />

          <div
            className="absolute top-0 left-1/2 -translate-x-1/2 pointer-events-none z-0"
            style={{
              width: '600px',
              height: '300px',
              background: 'radial-gradient(ellipse at 50% 0%, hsla(22, 100%, 50%, 0.07) 0%, transparent 70%)',
              filter: 'blur(32px)',
            }}
          />
          <Spotlight />

          {/* All content sits above background layers */}
          {/* Badge */}
          <div className="relative z-10 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-lava/10 border border-lava/20 text-xs text-lava font-medium mb-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
            <Flame className="w-3 h-3" />
            EdgeSQL — Powered by Cloudflare D1
          </div>

          {/* Headline */}
          <h1 className="relative z-10 text-5xl md:text-6xl font-bold text-foreground text-balance leading-[1.08] mb-5 animate-in fade-in slide-in-from-bottom-3 duration-700 delay-100">
            The edge database<br />
            <span
              style={{
                backgroundImage: 'linear-gradient(110deg, oklch(0.58 0.2 38) 0%, var(--lava) 30%, oklch(0.76 0.16 54) 50%, var(--lava) 70%, oklch(0.58 0.2 38) 100%)',
                backgroundSize: '300% auto',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
                display: 'inline-block',
                animation: 'shimmer-sweep 5s ease-in-out infinite',
              }}
            >that outperforms everything.</span>
          </h1>
          <p className="text-muted-foreground text-lg text-balance max-w-xl leading-relaxed mb-10 animate-in fade-in slide-in-from-bottom-3 duration-700 delay-200">
            Sub-10ms globally. SQLite-compatible. Drizzle-ready. No connection pools, no cold starts, no limits.
          </p>

          {/* Stat pills */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-12 animate-in fade-in duration-700 delay-300">
            <StatPill icon={Zap}      label="p99 latency"       value="< 8ms"   color="var(--lava)" />
            <StatPill icon={Globe}    label="edge nodes"        value="312+"    color="#82aaff" />
            <StatPill icon={Shield}   label="auth"              value="Web Crypto" color="#c3e88d" />
            <StatPill icon={Activity} label="uptime"            value="99.99%"  color="#c792ea" />
            <StatPill icon={Package}  label="Drizzle / Prisma"  value="native"  color="#f78c6c" />
          </div>

          {/* Architecture diagram */}
          <div className="w-full max-w-3xl mb-12 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-400">
            <ArchDiagram />
          </div>

          {/* Two-column: terminal + snippet */}
          <div className="w-full max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-4 mb-12 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-500 text-left">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Terminal className="w-3 h-3" /> Provision in seconds
              </p>
              <AnimatedTerminal />
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Code2 className="w-3 h-3" /> Connect instantly
              </p>
              <SyntaxBlock lang="js" code={`import { createClient } from '@lavaflow/client'

const db = createClient({
  apiKey: process.env.LAVAFLOW_SECRET,
  database: 'my-app',
})

// Drizzle ORM — drop-in compatible
import { drizzle } from 'drizzle-orm/lavaflow'
export const drizzleDb = drizzle(db)

// Query the edge
const { results } = await db.query(
  "SELECT * FROM users LIMIT 10"
)`} />
            </div>
          </div>

          {/* Compat logos */}
          <div className="flex flex-wrap items-center justify-center gap-6 mb-14 animate-in fade-in duration-700 delay-600">
            {[
              { label: 'Drizzle ORM', color: '#c3e88d' },
              { label: 'Prisma',      color: '#82aaff' },
              { label: 'SQLAlchemy',  color: '#f78c6c' },
              { label: 'Kysely',      color: '#c792ea' },
              { label: 'Knex.js',     color: 'var(--lava)' },
              { label: 'REST / HTTP', color: '#82aaff' },
            ].map(c => (
              <div key={c.label} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Check className="w-3 h-3" style={{ color: c.color }} />
                {c.label}
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="flex flex-col items-center gap-3 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-700">
            <Button
              size="lg"
              onClick={() => setStep(0) /* stays on step 0, scrolls to wizard */}
              className="h-13 px-8 text-base lava-gradient text-lava-foreground border-0 hover:opacity-90 transition-opacity gap-2 font-semibold glow-lava"
            >
              Start building <ArrowRight className="w-4 h-4" />
            </Button>
            <p className="text-xs text-muted-foreground">Free tier available. No credit card required.</p>
          </div>
        </div>
      )}

      {/* ── Wizard ─────────────────────────────────────────────── */}
      <div className={cn('flex flex-col items-center px-4 pb-24', step === 0 ? 'pt-6' : 'pt-20')}>
        <div className="w-full max-w-2xl">
          <StepIndicator current={step} />

          {/* Step 0: project name */}
          {step === 0 && (
            <div className="space-y-5 animate-in fade-in slide-in-from-bottom-3 duration-400">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Project name</Label>
                <Input
                  autoFocus
                  placeholder="my-app"
                  value={projectName}
                  onChange={e => setProjectName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && projectName.trim() && setStep(1)}
                  className="h-12 text-base bg-input border-border font-medium"
                />
                {projectName && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Database className="w-3 h-3" />
                    Database identifier: <code className="font-mono text-foreground bg-muted px-1 py-0.5 rounded ml-1">{dbSlug}</code>
                  </p>
                )}
              </div>
              <Button
                onClick={() => setStep(1)}
                disabled={!projectName.trim()}
                className="w-full h-11 lava-gradient text-lava-foreground border-0 hover:opacity-90 gap-2 font-medium"
              >
                Continue <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Step 1: use case */}
          {step === 1 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-400">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {USE_CASES.map((uc) => {
                  const Icon = uc.icon
                  const selected = selectedUseCase === uc.id
                  return (
                    <button
                      key={uc.id}
                      onClick={() => setSelectedUseCase(uc.id)}
                      className={cn(
                        'flex items-start gap-3 p-4 rounded-xl border text-left transition-all duration-200',
                        selected
                          ? 'border-lava bg-lava/5 ring-1 ring-lava/30 shadow-sm'
                          : 'border-border bg-card hover:border-lava/30 hover:bg-lava/[0.02]'
                      )}
                    >
                      <div className={cn('p-2 rounded-lg flex-shrink-0 mt-0.5 transition-colors', selected ? 'bg-lava/15 text-lava' : 'bg-muted text-muted-foreground')}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-foreground">{uc.title}</p>
                          {selected && <Check className="w-3.5 h-3.5 text-lava flex-shrink-0" />}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{uc.description}</p>
                      </div>
                    </button>
                  )
                })}
              </div>

              {useCase && (
                <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <GitBranch className="w-3 h-3" /> Starter schema
                  </p>
                  <SyntaxBlock lang="sql" code={useCase.starterSql} />
                </div>
              )}

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(0)}>Back</Button>
                <Button
                  onClick={() => setStep(2)}
                  disabled={!selectedUseCase}
                  className="flex-1 h-10 lava-gradient text-lava-foreground border-0 hover:opacity-90 gap-2 font-medium"
                >
                  Continue <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: provision */}
          {step === 2 && (
            <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-400">
              <div className="p-5 rounded-xl border border-border bg-card space-y-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Provisioning summary</p>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Project',  value: projectName },
                    { label: 'Database', value: dbSlug, mono: true },
                    { label: 'Engine',   value: 'Cloudflare D1 (SQLite)' },
                    { label: 'Network',  value: '312 edge nodes globally' },
                    { label: 'Use case', value: useCase?.title ?? '' },
                    { label: 'Auth',     value: 'Web Crypto · HMAC-SHA256' },
                  ].map(item => (
                    <div key={item.label}>
                      <p className="text-[11px] text-muted-foreground">{item.label}</p>
                      <p className={cn('text-sm font-medium text-foreground mt-0.5', item.mono && 'font-mono')}>{item.value}</p>
                    </div>
                  ))}
                </div>

                {/* Provision steps animation */}
                {provisioning && (
                  <div className="pt-2 border-t border-border space-y-2.5 animate-in fade-in duration-300">
                    {PROVISION_STEPS.map((s, i) => (
                      <div key={s} className="flex items-center gap-3">
                        <div className={cn(
                          'w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300',
                          i < provisionStep  ? 'bg-success'                        : '',
                          i === provisionStep ? 'border-2 border-lava animate-pulse' : '',
                          i > provisionStep  ? 'border border-border'              : '',
                        )}>
                          {i < provisionStep && <Check className="w-2.5 h-2.5 text-success-foreground" />}
                        </div>
                        <p className={cn(
                          'text-xs transition-colors',
                          i <= provisionStep ? 'text-foreground' : 'text-muted-foreground'
                        )}>{s}</p>
                      </div>
                    ))}
                  </div>
                )}

                {!provisioning && (
                  <div className="pt-2 border-t border-border space-y-1.5">
                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">What gets created</p>
                    {[
                      'EdgeSQL database bound to Cloudflare D1',
                      'Scoped API key with db:read, db:write, db:admin',
                      'Connection string and REST endpoint',
                      'Starter schema for your use case',
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-lava flex-shrink-0" />
                        <p className="text-xs text-muted-foreground">{item}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {error && (
                <p className="text-sm text-destructive bg-destructive/8 border border-destructive/20 px-3 py-2.5 rounded-lg">{error}</p>
              )}

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} disabled={provisioning}>Back</Button>
                <Button
                  onClick={handleProvision}
                  disabled={provisioning}
                  className="flex-1 h-11 lava-gradient text-lava-foreground border-0 hover:opacity-90 gap-2 font-medium"
                >
                  {provisioning ? <><RefreshCw className="w-4 h-4 animate-spin" /> Provisioning...</> : <><Database className="w-4 h-4" /> Provision database</>}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: connect */}
          {step === 3 && provisionDone && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-400">
              {/* Success */}
              <div className="flex items-center gap-3 p-4 rounded-xl border border-success/30 bg-success/8">
                <div className="w-8 h-8 rounded-full bg-success/15 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-success" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">Database live on the edge</p>
                  <p className="text-xs text-muted-foreground">{dbName} · 312 nodes · ready in &lt;2s</p>
                </div>
                <div className="ml-auto flex items-center gap-1.5 text-[10px] font-mono text-success">
                  <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                  LIVE
                </div>
              </div>

              {/* API key */}
              <div className="p-4 rounded-xl border border-lava/30 bg-lava/5 space-y-2.5">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-lava flex items-center gap-1.5">
                    <Lock className="w-3 h-3" /> Your API key — save this now
                  </p>
                  <Badge variant="secondary" className="text-[10px]">Shown once</Badge>
                </div>
                <div className="flex items-center gap-2 bg-[#0d1117] border border-[oklch(0.24_0.006_260)] rounded-lg px-3 py-2.5">
                  <code className="flex-1 text-[12px] font-mono text-[#c3e88d] break-all">{apiKey}</code>
                  <CopyButton text={apiKey ?? ''} />
                </div>
                <p className="text-[11px] text-muted-foreground">Store in <code className="font-mono bg-muted px-1 rounded">LAVAFLOW_SECRET</code> — this key will not be shown again.</p>
              </div>

              {/* Connection string */}
              <div className="flex items-center gap-2 bg-muted/60 border border-border rounded-lg px-3 py-2.5">
                <code className="flex-1 text-[12px] font-mono text-foreground">https://api.lavaflowstreams.pro/query</code>
                <CopyButton text="https://api.lavaflowstreams.pro/query" />
              </div>

              {/* SDK code snippets */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Workflow className="w-3 h-3" /> Connect your stack
                  </p>
                  <div className="flex items-center gap-1 p-0.5 rounded-lg bg-muted/60 border border-border">
                    {(Object.keys(LANG_ICONS) as Lang[]).map(lang => (
                      <button
                        key={lang}
                        onClick={() => setCodeLang(lang)}
                        className={cn(
                          'px-2.5 py-1 rounded-md text-[11px] font-mono font-semibold transition-all',
                          codeLang === lang
                            ? 'bg-lava text-lava-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                        )}
                      >
                        {LANG_ICONS[lang]}
                      </button>
                    ))}
                  </div>
                </div>
                <SyntaxBlock lang={codeLang === 'cURL' ? 'bash' : codeLang === 'Python' ? 'python' : 'js'} code={snippets[codeLang]} />
              </div>

              {/* ORM compat strip */}
              <div className="grid grid-cols-3 gap-2.5">
                {[
                  { icon: Package,    label: 'Drizzle ORM',  desc: 'drizzle-orm/lavaflow' },
                  { icon: Database,   label: 'Prisma',        desc: 'prisma-lavaflow-adapter' },
                  { icon: Terminal,   label: 'REST / HTTP',   desc: 'Any language, any framework' },
                ].map(item => (
                  <div key={item.label} className="p-3 rounded-xl border border-border bg-card hover:border-lava/30 transition-colors">
                    <item.icon className="w-4 h-4 text-muted-foreground mb-2" />
                    <p className="text-xs font-semibold text-foreground">{item.label}</p>
                    <p className="text-[10px] font-mono text-muted-foreground mt-0.5">{item.desc}</p>
                  </div>
                ))}
              </div>

              {/* Next steps */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { href: '/playground', icon: Terminal,  label: 'Open SQL Console',   desc: 'Write and run queries live' },
                  { href: '/portal',     icon: Database,  label: 'View My Databases',  desc: 'Manage tables and keys' },
                  { href: '/dashboard',  icon: Code2,     label: 'Admin Dashboard',    desc: 'Full platform controls' },
                ].map(item => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="flex flex-col items-start gap-2 p-4 rounded-xl border border-border bg-card hover:border-lava/30 hover:bg-lava/5 transition-all group"
                  >
                    <item.icon className="w-4 h-4 text-muted-foreground group-hover:text-lava transition-colors" />
                    <div>
                      <p className="text-xs font-semibold text-foreground">{item.label}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{item.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
